import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Factory, ShieldCheck } from 'lucide-react'
import { Button, Card, EmptyState } from '@/components/ui'
import { useAuth } from '@/context/AuthContext'
import { useT } from '@/i18n/I18nProvider'
import { cn } from '@/lib/utils'

/**
 * In place of a screen, for somebody who has been given no plant.
 *
 * Every list in the application is cut to the reader's plants, so without one
 * every screen is empty — and an empty screen reads as "nothing has happened"
 * or as "it is broken", both wrong. This says the one true thing instead, and
 * who can change it. The profile stays reachable: it is about the person, not
 * a plant.
 *
 * "Provjeri ponovo" re-reads the session. Which plants somebody has travels on
 * it, so the administrator giving them one changes nothing on this screen
 * until it is read again — and the person standing in front of it has just
 * been told to go and ask for exactly that.
 *
 * `compact` is the same sentence as a strip above a screen that still makes
 * sense without a plant — administration, where the person may need to see
 * that their own list is empty for this reason.
 */
export function NoPlantNotice({ compact = false }: { compact?: boolean }) {
  const t = useT()
  const { refreshUser } = useAuth()
  const [checking, setChecking] = useState(false)

  if (compact) {
    return (
      <p
        role="status"
        className={cn(
          'mb-4 flex items-start gap-2.5 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-sm',
          'text-amber-900 dark:text-amber-200',
        )}
      >
        <Factory className="mt-0.5 size-4 shrink-0" aria-hidden />
        {t('plants.none.title')}
      </p>
    )
  }

  return (
    <Card>
      <EmptyState
        icon={<Factory className="size-8" aria-hidden />}
        title={t('plants.none.title')}
        description={t('plants.none.body')}
        action={
          <div className="flex flex-wrap justify-center gap-2">
            <Button
              loading={checking}
              onClick={() => {
                setChecking(true)
                refreshUser()
                  .catch(() => {
                    // Offline or signed out; the screen simply stays as it is.
                  })
                  .finally(() => setChecking(false))
              }}
            >
              {t('plants.none.retry')}
            </Button>
            <Button variant="secondary" nativeButton={false} render={<Link to="/profil" />}>
              {t('account.myAccount')}
            </Button>
          </div>
        }
      />
    </Card>
  )
}

/**
 * Where a page would be, for an administrator who is not the glavni
 * administrator.
 *
 * The tabs are already hidden from them; this is for a bookmark or a link
 * pasted from somebody who can see it. Said rather than redirected, for the
 * reason `ModuleDisabled` gives: the person did nothing wrong, and landing on
 * another screen without a word reads as the link being broken.
 */
export function SuperAdminOnly() {
  const t = useT()

  return (
    <Card>
      <EmptyState
        icon={<ShieldCheck className="size-8" aria-hidden />}
        title={t('admin.locked.page.title')}
        description={t('admin.locked.page.body')}
        action={
          <Button variant="secondary" nativeButton={false} render={<Link to="/admin/korisnici" />}>
            {t('admin.nav.users')}
          </Button>
        }
      />
    </Card>
  )
}
